import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { chunk } from 'remeda';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { RegionService } from '../../root/region.service';
import { CalenderDayHitch, DriversHitchCalendarDay, Region } from '../../types';
import { OwnerDashboardService } from '../owner-dashboard.service';

class CalendarDay {
  public date: Date;
  public title: string;
  public isPastDate: boolean;
  public isToday: boolean;

  constructor(d: Date) {
    this.date = d;
    this.isPastDate = d.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
    this.isToday = d.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
  }
}

@Component({
  selector: 'lohi-driver-hitch-calendar',
  templateUrl: './driver-hitch-calendar.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverHitchCalendarComponent implements OnInit, OnDestroy {
  public today = new Date();
  private destroy$$ = new Subject();
  public regions$: Observable<Region[]>;
  public calendar: CalendarDay[] = [];
  public currentMonth;
  public driversHitchCalendarWeeks$$ = new BehaviorSubject<DriversHitchCalendarDay[][]>([]);
  public regionsFilterControl = new FormControl(-1);
  public driverHitchCalenderData: DriversHitchCalendarDay[];
  public isDetailsViewOpen = false;
  public selectedDay: DriversHitchCalendarDay | null = null;
  public isOpenEventToggle = false;
  public timeZoneAbbreviation: string;
  public currentDateTime;
  public isShowArchivedCancelHitchAndShift$ = new BehaviorSubject<boolean>(false);

  constructor(
    private dashboardService: OwnerDashboardService,
    private regionService: RegionService,
    private datePipe: DatePipe,
  ) {}

  public ngOnInit(): void {
    this.dashboardService.driversHitchCalendar$
      .pipe(
        takeUntil(this.destroy$$),
        filter((data) => !!data),
      )
      .subscribe((resp) => {
        this.driverHitchCalenderData = resp;
        this.driversHitchCalendarWeeks$$.next(chunk(resp, 7));
      });
    this.regions$ = this.regionService.regions$;
    this.setupFormControls();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    this.timeZoneAbbreviation = this.getTimeZoneAbbreviation(timezone);
    const now = new Date();
    this.currentDateTime = this.datePipe.transform(now, 'fullDate') + ' ' + this.datePipe.transform(now, 'shortTime');
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  private setupFormControls() {
    this.regionsFilterControl.valueChanges.pipe(takeUntil(this.destroy$$)).subscribe((value) => {
      if (value === -1) {
        this.driversHitchCalendarWeeks$$.next(chunk(this.driverHitchCalenderData, 7));
      } else {
        this.driversHitchCalendarWeeks$$.next(
          chunk(
            this.driverHitchCalenderData.map((value1) => {
              return {
                ...value1,
                excusedDrivers: value1.excusedDrivers?.filter((driver) => driver.regionId === value),
                noShowDrivers: value1.noShowDrivers?.filter((driver) => driver.regionId === value),
                hitchesStartingToday: value1.hitchesStartingToday?.filter((hitch) => hitch.regionId === value),
                hitchesEndingToday: value1.hitchesEndingToday?.filter((hitch) => hitch.regionId === value),
                hitchesFullDay: value1.hitchesFullDay?.filter((hitch) => hitch.regionId === value),
                driverStatusCount: value1.driverStatusCount,
              };
            }),
            7,
          ),
        );
      }
    });
  }

  public toggleHitchDetails(day: DriversHitchCalendarDay): void {
    if (this.selectedDay === day) {
      this.isDetailsViewOpen = !this.isDetailsViewOpen;
    } else {
      this.selectedDay = day;
      this.isDetailsViewOpen = true;
    }
  }

  public toggleContentEvents(): void {
    this.isOpenEventToggle = !this.isOpenEventToggle;
  }

  public closeDetailsView(): void {
    this.isDetailsViewOpen = false;
    this.selectedDay = null;
  }

  public get hitchesSelectedDay(): CalenderDayHitch[] {
    if (this.selectedDay) {
      const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

      return [
        ...this.selectedDay.hitchesEndingToday.filter((hitch) => !hitch.archivedAt || showArchived),
        ...this.selectedDay.hitchesFullDay.filter((hitch) => !hitch.archivedAt || showArchived),
      ];
    }
    return [];
  }

  public getRegionName(regionId): string {
    let regionName = 'Unknown Region';
    this.regions$.subscribe((regions) => {
      const region = regions.find((r) => r.id === regionId);
      if (region) {
        regionName = region.name;
      }
    });
    return regionName;
  }

  public trackByKeyHitch(index: number, hitch: any): number {
    return hitch.id;
  }
  public trackByKeyNoShowDriver(index: number, hitch: any): number {
    return hitch.id;
  }
  public trackByKeyExcusedDriver(index: number, hitch: any): number {
    return hitch.id;
  }

  public getTimeZoneAbbreviation(timezone): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      timeZoneName: 'short',
    };
    const timeZoneName = new Intl.DateTimeFormat('en-US', options).format(now);
    return timeZoneName.split(' ').pop() || '';
  }

  public getContainerClass(hitchData: any): string {
    let classHitch = hitchData.isDayShift ? 'bg-yellow-300 text-black' : 'bg-black text-white';

    if (hitchData.archivedAt) {
      classHitch += ' opacity-25';
    }

    return classHitch;
  }

  public getMainIcon(hitchData: any): string {
    return hitchData.isDayShift ? 'light_mode' : 'dark_mode';
  }

  public toggleArchiveHitchShift(): void {
    const currentStatus = this.isShowArchivedCancelHitchAndShift$.value;
    this.isShowArchivedCancelHitchAndShift$.next(!currentStatus);
  }

  public getVisibleNoShowDrivers(): any[] {
    const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

    if (!this.selectedDay.noShowDrivers) {
      return [];
    }

    return this.selectedDay.noShowDrivers.filter((driver) => !driver.archivedAt || showArchived);
  }

  public getVisibleExcusedDrivers(): any[] {
    const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

    if (!this.selectedDay.excusedDrivers) {
      return [];
    }

    return this.selectedDay.excusedDrivers.filter((driver) => !driver.archivedAt || showArchived);
  }

  public hasNonArchivedHitch(): boolean {
    if (!this.selectedDay.noShowDrivers) {
      return false;
    }

    return this.selectedDay.noShowDrivers.some((driver) => !driver.archivedAt);
  }
}
